/**
 * Layout `beforeLoad`s (session, organization list, organization) run on every
 * navigation, including a filter click that only changes the search string.
 * On the client, a route that is staying mounted reuses what it fetched last
 * time instead of making three server calls per click.
 *
 * Entering a route, the server render and anything after
 * `clearRouteContextCache()` (call it before `router.invalidate()` when the
 * session or organization changed) always fetch fresh.
 */

const MAX_AGE_MS = 5 * 60_000;
const cache = new Map<string, { at: number; value: Promise<unknown> }>();

export function reuseWhileStaying<T>(
	key: string,
	cause: "enter" | "stay" | "preload",
	load: () => Promise<T>,
): Promise<T> {
	if (typeof window === "undefined") {
		return load();
	}
	const hit = cache.get(key);
	if (cause === "stay" && hit && Date.now() - hit.at < MAX_AGE_MS) {
		return hit.value as Promise<T>;
	}
	const value = load();
	cache.set(key, { at: Date.now(), value });
	// A failed call must not be served to the next navigation.
	value.catch(() => cache.delete(key));
	return value;
}

export function clearRouteContextCache() {
	cache.clear();
}
